export interface SecurityEvent {
  type: 'auth' | 'error';
  action: string;
  userId?: string;
  userEmail?: string;
  details?: Record<string, any>;
  severity: 'low' | 'medium' | 'high' | 'critical';
  source: string;
  timestamp?: string;
}

class SecurityMonitor {
  private events: SecurityEvent[] = [];
  private maxEvents = 100; // Reduced from 1000

  logEvent(event: SecurityEvent) {
    // Only log essential security events
    const allowedActions = [
      'signup_success',
      'signup_failed', 
      'login_success',
      'login_failed',
      'logout_success',
      'logout_failed',
      'session_expired',
      'password_recovery',
      'password_reset_requested',
      'password_reset_failed'
    ];

    if (!allowedActions.includes(event.action)) {
      return; // Skip non-essential events
    }

    const enhancedEvent = {
      ...event,
      timestamp: event.timestamp || new Date().toISOString(),
    };

    this.events.push(enhancedEvent);
    
    // Keep only the last maxEvents
    if (this.events.length > this.maxEvents) {
      this.events = this.events.slice(-this.maxEvents);
    }

    // Log to console based on severity (for development only)
    const logLevel = this.getLogLevel(event.severity);
    console[logLevel]('SecurityMonitor:', {
      action: enhancedEvent.action,
      type: enhancedEvent.type,
      severity: enhancedEvent.severity,
      timestamp: enhancedEvent.timestamp
    });

    // Note: Removed localStorage storage for security reasons
    // In production, consider sending critical events to a secure backend endpoint
  }

  logAuthEvent(action: string, userId?: string, userEmail?: string, details?: Record<string, any>) {
    this.logEvent({
      type: 'auth',
      action,
      userId,
      userEmail,
      details,
      severity: this.getAuthEventSeverity(action),
      source: 'auth_system'
    });
  }

  // Removed logAccessEvent method - access logging is not essential for security

  logErrorEvent(message: string, source: string, userId?: string, details?: Record<string, any>) {
    // Only log authentication-related errors
    const authRelatedSources = ['auth_context', 'protected_route', 'role_based_route'];
    if (!authRelatedSources.includes(source)) {
      return; // Skip non-auth errors
    }

    this.logEvent({
      type: 'error',
      action: 'auth_error',
      userId,
      details: {
        message,
        source,
        ...details
      },
      severity: 'medium',
      source
    });
  }

  // Removed logSuspiciousEvent method - not essential for basic security

  private getLogLevel(severity: string): 'log' | 'warn' | 'error' {
    switch (severity) {
      case 'critical':
      case 'high':
        return 'error';
      case 'medium':
        return 'warn';
      default:
        return 'log';
    }
  }

  private getAuthEventSeverity(action: string): 'low' | 'medium' | 'high' | 'critical' {
    const highSeverityActions = [
      'login_failed',
      'signup_failed',
      'session_expired',
      'password_reset_failed'
    ];

    const mediumSeverityActions = [
      'logout_success',
      'password_reset_requested'
    ];

    if (highSeverityActions.includes(action)) {
      return 'high';
    } else if (mediumSeverityActions.includes(action)) {
      return 'medium';
    }

    return 'low';
  }

  // Get recent events for debugging (development only)
  getRecentEvents(limit = 20): SecurityEvent[] {
    return this.events.slice(-limit);
  }

  // Get events by type
  getEventsByType(type: SecurityEvent['type'], limit = 20): SecurityEvent[] {
    return this.events
      .filter(event => event.type === type)
      .slice(-limit);
  }

  // Export security log (development only)
  exportSecurityLog(): string {
    return JSON.stringify(this.events, null, 2);
  }

  // Clear all events (development only)
  clearEvents() {
    this.events = [];
    // Removed localStorage cleanup
  }
}

export const securityMonitor = new SecurityMonitor();
